import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Collapsible,
  DisplayText,
  IllusCredit_1,
  Image,
  LoadingPlaceholder,
  theme,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { Credit } from '../../../graphql/types';
import { CreditBalanceDetails } from '../CreditBalanceDetails';
import { useViewDetailsTelemetry } from './CreditOverview.telemetry';

export const CreditOverviewBalance = ({
  creditAmount,
  creditDetails,
  isLoadingCredits,
}: CreditOverviewBalanceProps) => {
  const { t, formatPrice } = useLocalizationContext();
  const [visibleCredits, setVisibleCredits] = React.useState(false);

  useViewDetailsTelemetry(visibleCredits);

  return (
    <View
      style={styles.cardContainer}
      testID="account.available-credit-details"
    >
      <View style={styles.card}>
        <Image
          source={IllusCredit_1}
          style={styles.image}
          accessibilityIgnoresInvertColors
        />
        <View>
          <BodyText size={2}>{t('account.credit.label')}</BodyText>
          {isLoadingCredits ? (
            <View style={styles.loadingContainer}>
              <LoadingPlaceholder
                rows={1}
                rowHeight={36}
                palette="lightestGreen"
              />
            </View>
          ) : (
            <DisplayText size={3}>
              {formatPrice(creditAmount, 'USD')}
            </DisplayText>
          )}
        </View>
      </View>

      {creditAmount > 0 && (
        <Collapsible
          style={styles.fullWidth}
          options={{ hideIcon: true }}
          onChange={setVisibleCredits}
        >
          <Collapsible.Details>
            <CreditBalanceDetails creditDetails={creditDetails} />
          </Collapsible.Details>
          {visibleCredits ? (
            <Collapsible.Button
              containerProps={styles.centerSelf}
              style={styles.closeDetails}
              width={136}
              testID="account.available-credit-close-details-button"
              palette="secondary"
              accessibilityRole="button"
              accessibilityLabel={t('account.credit.close-details')}
              accessibilityHint={t('account.credit.close-details')}
            >
              {t('account.credit.close-details')}
            </Collapsible.Button>
          ) : (
            <Collapsible.Button
              containerProps={styles.centerSelf}
              width={136}
              testID="account.available-credit-view-details-button"
              palette="secondary"
              accessibilityRole="button"
              accessibilityLabel={t('account.credit.view-details')}
              accessibilityHint={t('account.credit.view-details')}
            >
              {t('account.credit.view-details')}
            </Collapsible.Button>
          )}
        </Collapsible>
      )}
    </View>
  );
};

type CreditOverviewBalanceProps = {
  readonly creditAmount: number;
  readonly creditDetails: readonly Credit[];
  readonly isLoadingCredits?: boolean;
};

const styles = StyleSheet.create({
  cardContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xlarge,
    marginTop: theme.spacing.medium,
    marginBottom: theme.spacing.xlarge,
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.CUCUMBER,
  },
  image: {
    height: 110,
    width: 110,
    marginRight: theme.spacing.medium,
  },
  loadingContainer: {
    width: 100,
    height: 36,
  },
  centerSelf: {
    alignSelf: 'center',
  },
  fullWidth: {
    width: '100%',
  },
  closeDetails: {
    marginTop: theme.spacing.medium,
  },
});
